import { useCallback, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Question } from '../../api/types';

export interface QuestionCardProps {
  questions: Question[];
  onSubmit: (answers: Record<string, string>) => void;
  disabled?: boolean;
}

const useStyles = makeStyles(theme => ({
  card: {
    margin: theme.spacing(1, 0),
    border: `1px solid ${theme.palette.secondary.main}`,
  },
  header: {
    fontWeight: 600,
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(0.5),
  },
  questionText: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  optionDescription: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(4),
    marginTop: -theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    wordBreak: 'break-word',
  },
  otherTextField: {
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    // Prevent overflow: reduce width by the left margin so it stays contained
    width: `calc(100% - ${theme.spacing(4)}px)`,
  },
  submitButton: {
    marginTop: theme.spacing(1),
  },
  questionSection: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  selectionType: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginBottom: theme.spacing(0.5),
  },
}));

const OTHER_OPTION = '__other__';

export const QuestionCard = ({
  questions,
  onSubmit,
  disabled = false,
}: QuestionCardProps) => {
  const classes = useStyles();
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [multiSelections, setMultiSelections] = useState<
    Record<string, Set<string>>
  >({});
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({});
  const [useOther, setUseOther] = useState<Record<string, boolean>>({});

  const handleRadioChange = useCallback(
    (questionText: string, value: string) => {
      if (value === OTHER_OPTION) {
        setUseOther(prev => ({ ...prev, [questionText]: true }));
        setSelections(prev => ({ ...prev, [questionText]: '' }));
      } else {
        setUseOther(prev => ({ ...prev, [questionText]: false }));
        setSelections(prev => ({ ...prev, [questionText]: value }));
      }
    },
    [],
  );

  const handleCheckboxChange = useCallback(
    (questionText: string, label: string, checked: boolean) => {
      setMultiSelections(prev => {
        const current = new Set(prev[questionText] || []);
        if (label === OTHER_OPTION) {
          setUseOther(p => ({ ...p, [questionText]: checked }));
          if (!checked) {
            current.delete(OTHER_OPTION);
          } else {
            current.add(OTHER_OPTION);
          }
        } else if (checked) {
          current.add(label);
        } else {
          current.delete(label);
        }
        return { ...prev, [questionText]: current };
      });
    },
    [],
  );

  const handleOtherTextChange = useCallback(
    (questionText: string, text: string) => {
      setOtherTexts(prev => ({ ...prev, [questionText]: text }));
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    const answers: Record<string, string> = {};

    for (const q of questions) {
      if (q.multiSelect) {
        const selected = multiSelections[q.question] || new Set();
        const labels: string[] = [];
        for (const label of selected) {
          if (label === OTHER_OPTION) {
            const otherText = otherTexts[q.question]?.trim();
            if (otherText) labels.push(otherText);
          } else {
            labels.push(label);
          }
        }
        answers[q.question] = labels.join(', ');
      } else {
        if (useOther[q.question]) {
          answers[q.question] = otherTexts[q.question]?.trim() || '';
        } else {
          answers[q.question] = selections[q.question] || '';
        }
      }
    }

    onSubmit(answers);
  }, [questions, selections, multiSelections, otherTexts, useOther, onSubmit]);

  const hasAllAnswers = questions.every(q => {
    if (q.multiSelect) {
      const selected = multiSelections[q.question];
      return selected && selected.size > 0;
    }
    if (useOther[q.question]) {
      return (otherTexts[q.question]?.trim() || '').length > 0;
    }
    return (selections[q.question] || '').length > 0;
  });

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent>
        {questions.map(q => (
          <div key={q.question} className={classes.questionSection}>
            {q.header && (
              <Typography variant="subtitle2" className={classes.header}>
                {q.header}
              </Typography>
            )}
            <Typography variant="body1" className={classes.questionText}>
              {q.question}
            </Typography>
            <Typography className={classes.selectionType}>
              {q.multiSelect ? '(Múltipla escolha)' : '(Seleção única)'}
            </Typography>

            {q.multiSelect ? (
              <FormGroup role="group" aria-label={q.question}>
                {q.options.map(opt => (
                  <div key={opt.label}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            multiSelections[q.question]?.has(opt.label) || false
                          }
                          onChange={(_e, checked) =>
                            handleCheckboxChange(q.question, opt.label, checked)
                          }
                          disabled={disabled}
                        />
                      }
                      label={opt.label}
                    />
                    {opt.description && (
                      <Typography className={classes.optionDescription}>
                        {opt.description}
                      </Typography>
                    )}
                  </div>
                ))}
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useOther[q.question] || false}
                        onChange={(_e, checked) =>
                          handleCheckboxChange(
                            q.question,
                            OTHER_OPTION,
                            checked,
                          )
                        }
                        disabled={disabled}
                      />
                    }
                    label="Outro"
                  />
                  {useOther[q.question] && (
                    <TextField
                      className={classes.otherTextField}
                      size="small"
                      variant="outlined"
                      placeholder="Digite sua resposta..."
                      value={otherTexts[q.question] || ''}
                      onChange={e =>
                        handleOtherTextChange(q.question, e.target.value)
                      }
                      disabled={disabled}
                      multiline
                      minRows={2}
                      maxRows={6}
                      aria-label={`Resposta personalizada para ${q.question}`}
                    />
                  )}
                </div>
              </FormGroup>
            ) : (
              <RadioGroup
                aria-label={q.question}
                value={
                  useOther[q.question]
                    ? OTHER_OPTION
                    : selections[q.question] || ''
                }
                onChange={(_e, value) => handleRadioChange(q.question, value)}
              >
                {q.options.map(opt => (
                  <div key={opt.label}>
                    <FormControlLabel
                      value={opt.label}
                      control={<Radio disabled={disabled} />}
                      label={opt.label}
                    />
                    {opt.description && (
                      <Typography className={classes.optionDescription}>
                        {opt.description}
                      </Typography>
                    )}
                  </div>
                ))}
                <div>
                  <FormControlLabel
                    value={OTHER_OPTION}
                    control={<Radio disabled={disabled} />}
                    label="Outro"
                  />
                  {useOther[q.question] && (
                    <TextField
                      className={classes.otherTextField}
                      size="small"
                      variant="outlined"
                      placeholder="Digite sua resposta..."
                      value={otherTexts[q.question] || ''}
                      onChange={e =>
                        handleOtherTextChange(q.question, e.target.value)
                      }
                      disabled={disabled}
                      multiline
                      minRows={2}
                      maxRows={6}
                      aria-label={`Resposta personalizada para ${q.question}`}
                    />
                  )}
                </div>
              </RadioGroup>
            )}
          </div>
        ))}

        <Button
          variant="contained"
          color="secondary"
          className={classes.submitButton}
          onClick={handleSubmit}
          disabled={disabled || !hasAllAnswers}
          aria-label="Enviar resposta"
        >
          Enviar resposta
        </Button>
      </CardContent>
    </Card>
  );
};
